<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import type { User } from '@/types/auth'

interface NavItem {
  labelKey: string
  route: string
  icon: string
  id: string
}

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()

const user = computed<User | null>(() => authStore.user as User | null)
const isAdmin = computed<boolean>(() => authStore.isAdmin as boolean)

// Sidebar collapse state - starts collapsed
const isCollapsed = ref<boolean>(true)
const sidebarRef = ref<HTMLElement | null>(null)
const emit = defineEmits<{
  'collapse-change': [collapsed: boolean]
}>()

const toggleCollapse = (): void => {
  isCollapsed.value = !isCollapsed.value
  emit('collapse-change', isCollapsed.value)
}

const handleDocumentClick = (event: MouseEvent): void => {
  // Only auto-collapse when sidebar is expanded
  if (isCollapsed.value) return

  const sidebarEl = sidebarRef.value
  if (!sidebarEl) return

  const target = event.target as Node | null
  // If click is outside the sidebar, collapse it
  if (target && !sidebarEl.contains(target)) {
    isCollapsed.value = true
    emit('collapse-change', true)
  }
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})

const navItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [
    { labelKey: 'nav.products', route: 'products', icon: '📦', id: 'productMenu' },
    { labelKey: 'nav.upload', route: 'upload', icon: '📤', id: 'uploadMenu' },
    { labelKey: 'nav.adhoc', route: 'adhoc', icon: '🔍', id: 'adhocMenu' },
    { labelKey: 'nav.reports', route: 'reports', icon: '📈', id: 'reportsMenu' }
  ]

  if (isAdmin.value) {
    items.push(
      { labelKey: 'nav.settings', route: 'settings', icon: '⚙️', id: 'settingsMenu' }
    )
  }
  
  return items
})

const isActiveRoute = (routeName: string): boolean => {
  if (routeName === 'reports') {
    return route.name === 'reports' || route.name === 'userReporting' || route.name === 'profileReporting'
  }
  if (routeName === 'settings') {
    return route.name === 'settings' || route.name === 'profiles' || route.name === 'users'
  }
  return route.name === routeName
}

const navigateTo = (routeName: string): void => {
  router.push({ name: routeName })
}

const userInitials = computed<string>(() => {
  if (!user.value) return 'U'
  const first = user.value.firstname?.[0] || ''
  const last = user.value.lastName?.[0] || ''
  return (first + last).toUpperCase() || 'U'
})

const logout = (): void => {
  authStore.logout()
}
</script>

<template>
  <aside ref="sidebarRef" :class="['sidebar', { collapsed: isCollapsed }]">
    <!-- Logo Section -->
    <div class="sidebar-header">
      <div class="header-row">
        <div class="logo">
          <span class="logo-icon">🌐</span>
          <span v-show="!isCollapsed" class="logo-text">Avalara</span>
        </div>
      </div>
      <span v-show="!isCollapsed" class="logo-subtitle">Trade Compliance</span>
    </div>

    <button
      class="collapse-btn"
      @click.stop="toggleCollapse"
      :title="isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')"
      :aria-label="isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')"
    >
      <span class="collapse-icon">{{ isCollapsed ? '›' : '‹' }}</span>
    </button>
    
    <!-- Navigation -->
    <nav class="sidebar-nav">
      <button
        v-for="item in navItems"
        :key="item.id"
        :class="['nav-item', { active: isActiveRoute(item.route) }]"
        @click="navigateTo(item.route)"
        :title="isCollapsed ? t(item.labelKey) : ''"
      >
        <span class="nav-icon">{{ item.icon }}</span>
        <span v-show="!isCollapsed" class="nav-text">{{ t(item.labelKey) }}</span>
      </button>
    </nav>
    
    <!-- User Section -->
    <div class="sidebar-footer">
      <button
        v-if="isCollapsed"
        class="collapsed-logout-btn"
        @click="logout"
        :title="t('sidebar.logout')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M10.75 3.5h4.5a2.25 2.25 0 0 1 2.25 2.25v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 0 15.25 5h-4.5a.75.75 0 0 0-.75.75v12.5c0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75v-3a.75.75 0 0 1 1.5 0v3a2.25 2.25 0 0 1-2.25 2.25h-4.5A2.25 2.25 0 0 1 8.5 18.25V5.75A2.25 2.25 0 0 1 10.75 3.5Zm5.97 7.97-1.72-1.72a.75.75 0 1 1 1.06-1.06l3 3a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 1 1-1.06-1.06l1.72-1.72H3.75a.75.75 0 0 1 0-1.5h12.97Z"
          />
        </svg>
      </button>
      <div v-else class="user-card">
        <div class="user-avatar">{{ userInitials }}</div>
        <div v-show="!isCollapsed" class="user-info">
          <span class="user-name">{{ user?.firstname }} {{ user?.lastName }}</span>
          <button class="logout-link" @click="logout">{{ t('sidebar.logout') }}</button>
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  width: 260px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-right: 1px solid #e2e8f0;
  box-shadow: 4px 0 16px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  transition: width 0.3s ease;
}

.sidebar.collapsed {
  width: 72px;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-icon {
  font-size: 1.5rem;
}

.logo-text {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.5px;
}

.logo-subtitle {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.sidebar-nav {
  flex: 1;
  padding: 1rem 0.75rem;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;
}

.nav-item:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.nav-item.active {
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 6px 14px rgba(37, 99, 235, 0.28);
}

.nav-icon {
  font-size: 1.125rem;
  width: 24px;
  text-align: center;
}

.nav-text {
  flex: 1;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
}

.user-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: white;
}

.collapsed-logout-btn {
  width: 100%;
  height: 40px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.collapsed-logout-btn svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.collapsed-logout-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #1d4ed8;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  display: block;
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logout-link {
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.75rem;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s;
}

.logout-link:hover {
  color: #2563eb;
}

/* Collapse Button */
.collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #94a3b8;
  font-size: 0.9rem;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  right: -12px;
  top: 96px;
  z-index: 110;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}

.collapse-btn:hover {
  background: #f8fafc;
  color: #2563eb;
  border-color: #cbd5e1;
}

.collapse-icon {
  line-height: 1;
  font-weight: 700;
}

/* Collapsed state adjustments */
.sidebar.collapsed .sidebar-header {
  padding: 1rem 0.75rem;
}

.sidebar.collapsed .header-row {
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sidebar.collapsed .logo {
  justify-content: center;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 0.75rem;
}

.sidebar.collapsed .nav-icon {
  width: auto;
}

.sidebar.collapsed .user-card {
  justify-content: center;
  padding: 0.75rem;
}
</style>
